import { useRef, useCallback, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { uploadImage, uploadVideo } from '../cloudinary';

// Register custom fonts
const Font = Quill.import('formats/font');
const Size = Quill.import('attributors/style/size');
const BlockEmbed = Quill.import('blots/block/embed');

class UploadedVideoBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute('controls', true);
    node.setAttribute('playsinline', true);
    node.setAttribute('preload', 'metadata');
    node.setAttribute('src', value);
    return node;
  }

  static value(node) {
    return node.getAttribute('src');
  }
}

UploadedVideoBlot.blotName = 'uploadedVideo';
UploadedVideoBlot.tagName = 'video';
UploadedVideoBlot.className = 'ql-uploaded-video';

Font.whitelist = [
  false, // default
  'serif', 'monospace',
  'playfair', 'lora', 'merriweather', 'cormorant',
  'raleway', 'nunito', 'josefin', 'crimson',
];
Quill.register(Font, true);
Size.whitelist = ['0.85rem', '1rem', '1.15rem', '1.35rem', '1.6rem', '2rem'];
Quill.register(Size, true);
Quill.register(UploadedVideoBlot, true);

const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'script',
  'color', 'background', 'align', 'direction',
  'list', 'bullet', 'indent',
  'blockquote', 'code-block',
  'link', 'image', 'video', 'uploadedVideo',
];

// Upload image files into the editor at the current cursor position
async function insertUploadedImages(editor, files) {
  for (const file of files) {
    try {
      const result = await uploadImage(file);
      const range = editor.getSelection(true);
      editor.insertEmbed(range.index, 'image', result.url);
      editor.setSelection(range.index + 1);
    } catch (err) {
      console.error('Image upload failed:', err);
      alert(`Failed to upload ${file.name}: ${err.message}`);
    }
  }
}

async function insertUploadedVideos(editor, files) {
  for (const file of files) {
    try {
      const result = await uploadVideo(file);
      const range = editor.getSelection(true);
      editor.insertEmbed(range.index, 'uploadedVideo', result.url);
      editor.setSelection(range.index + 1);
    } catch (err) {
      console.error('Video upload failed:', err);
      alert(`Failed to upload ${file.name}: ${err.message}`);
    }
  }
}

// Find any base64 images in the editor and replace them with Cloudinary URLs
async function replaceBase64Images(editor) {
  const imgs = editor.root.querySelectorAll('img[src^="data:"]');
  for (const img of imgs) {
    try {
      const blob = await fetch(img.src).then((r) => r.blob());
      const file = new File([blob], 'pasted-image.png', { type: blob.type });
      const result = await uploadImage(file);
      img.setAttribute('src', result.url);
    } catch (err) {
      console.error('Base64 image upload failed:', err);
    }
  }
  // Sync editor content back after src replacements
  editor.update();
}

export default function RichEditor({ value, onChange, placeholder, style }) {
  const quillRef = useRef(null);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.setAttribute('multiple', 'true');
    input.click();

    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (files.length === 0) return;
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      await insertUploadedImages(editor, files);
    };
  }, []);

  const videoHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'video/mp4,video/webm,video/quicktime,video/*');
    input.setAttribute('multiple', 'true');
    input.click();

    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (files.length === 0) return;
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      await insertUploadedVideos(editor, files);
    };
  }, []);

  // Intercept paste and drop to catch images that Quill would embed as base64
  const handlePasteOrDrop = useCallback((e) => {
    const items = e.clipboardData?.items || e.dataTransfer?.items;
    if (!items) return;

    const imageFiles = [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      const editor = quillRef.current?.getEditor();
      if (editor) insertUploadedImages(editor, imageFiles);
      return;
    }

    // For HTML pastes that may contain inline base64 images, let Quill handle
    // the paste then sweep for base64 srcs and replace them
    if (e.type === 'paste') {
      const html = e.clipboardData?.getData('text/html') || '';
      if (html.includes('data:image')) {
        setTimeout(() => {
          const editor = quillRef.current?.getEditor();
          if (editor) replaceBase64Images(editor);
        }, 100);
      }
    }
  }, []);

  // Attach paste/drop listeners to the Quill editor root
  const attachListeners = useCallback((ref) => {
    quillRef.current = ref;
    if (!ref) return;
    const root = ref.getEditor().root;
    root.addEventListener('paste', handlePasteOrDrop, true);
    root.addEventListener('drop', handlePasteOrDrop, true);
  }, [handlePasteOrDrop]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, false] }],
        [{ font: Font.whitelist.filter(Boolean) }],
        [{ size: Size.whitelist }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ script: 'sub' }, { script: 'super' }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }, { direction: 'rtl' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      handlers: {
        image: imageHandler,
        video: videoHandler,
      },
    },
    clipboard: {
      matchVisual: false,
    },
  }), [imageHandler, videoHandler]);

  return (
    <div className="rich-editor-wrapper" style={style}>
      <ReactQuill
        ref={attachListeners}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder || 'Start writing...'}
      />
    </div>
  );
}
